// Kiri's home state — shown when a conversation session is empty. Replaces
// the old "giant empty black panel" with a greeting, quick actions, a
// current-page/job context pill, page-specific suggestions, a static
// contextual tip and the user's own recent Kiri activity.
import { useEffect, useState } from "react";
import { Plus, Search, Sparkles, Zap, Lightbulb, X, ClipboardList, BookOpenText, Briefcase } from "lucide-react";
import { KiriCharacter } from "./KiriCharacter";
import { getSuggestedActions, currentPageLabel, getCreateMenu } from "@/lib/kiriSuggestions";

const TIPS = {
  job: "You can build today's site diary from the crew, plant and photos already logged on this job.",
  jobs: "Tell me a title and I'll prepare a job draft ready to review in Create Job.",
  financials: "Ask me which job has the lowest margin and I'll pull it straight from Financials.",
  timesheets: "I can tell you what's still outstanding before you run payroll.",
  safety: "I can take you straight to pre-starts or toolbox talks — just ask.",
  qa: "Ask me to find a QA record and I'll search this job for you.",
  quotes: "I can summarise your outstanding quotes whenever you need a quick read.",
};

const RECENT_META = {
  task_created: { Icon: ClipboardList, label: (d) => `Task added: ${d.text}` },
  diary_created: { Icon: BookOpenText, label: (d) => `Site diary — ${d.job_title || "job"}` },
  job_draft: { Icon: Briefcase, label: (d) => `Job draft — ${d.title || "untitled"}` },
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const QuickPill = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex items-center gap-1.5 bg-[#12161C] hover:bg-[#181D25] border border-white/5 text-zinc-200 hover:text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors"
  >
    <Icon className="w-3.5 h-3.5 text-[#FF7A3D]" /> {label}
  </button>
);

export function KiriHome({ user, pathname, jobTitle, onSend, onPrefill, recent }) {
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const pageLabel = currentPageLabel(pathname);
  const isJobPage = /^\/app\/jobs\/[^/]+/.test(pathname || "");
  const suggestions = getSuggestedActions(user?.role, pathname);
  const createMenu = getCreateMenu(user?.role);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const tipKey = isJobPage ? "job" : Object.keys(TIPS).find((k) => (pathname || "").includes(k)) || null;
  const tipText = tipKey ? TIPS[tipKey] : null;
  const [tipDismissed, setTipDismissed] = useState(false);

  useEffect(() => {
    if (!tipKey) return;
    setTipDismissed(localStorage.getItem(`fl_kiri_tip_seen_${tipKey}`) === "1");
  }, [tipKey]);

  const dismissTip = () => {
    if (tipKey) localStorage.setItem(`fl_kiri_tip_seen_${tipKey}`, "1");
    setTipDismissed(true);
  };

  return (
    <div className="pt-1" data-testid="kiri-empty">
      <div className="flex flex-col items-center text-center pt-2 pb-4">
        <KiriCharacter state="idle" size="xl" useHero />
        <div className="mt-3 text-lg font-display font-black text-white">Kia ora, {firstName}</div>
        <div className="text-sm text-zinc-400 mt-0.5">What are we getting done?</div>
      </div>

      {(jobTitle || pageLabel) && (
        <div className="mx-1 mb-3 flex items-center justify-between bg-[#12161C] border border-white/5 rounded-lg px-3 py-2" data-testid="kiri-context-pill">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
              {jobTitle ? "Current job" : "Currently viewing"}
            </div>
            <div className="text-sm text-white font-medium truncate">{jobTitle || pageLabel}</div>
          </div>
        </div>
      )}

      {!showCreateMenu ? (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <QuickPill icon={Plus} label="Create something" onClick={() => setShowCreateMenu(true)} testId="kiri-quick-create" />
          <QuickPill icon={Search} label="Find something" onClick={() => onPrefill("Find ")} testId="kiri-quick-find" />
          <QuickPill icon={Sparkles} label="Summarise" onClick={() => (jobTitle ? onSend("Summarise this job") : onPrefill("Summarise "))} testId="kiri-quick-summarise" />
          <QuickPill icon={Zap} label="What needs attention?" onClick={() => onSend("What needs attention?")} testId="kiri-quick-attention" />
        </div>
      ) : (
        <div className="mb-3 bg-[#12161C] border border-white/5 rounded-lg p-3" data-testid="kiri-create-menu">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wide text-zinc-300">What are we making?</div>
            <button onClick={() => setShowCreateMenu(false)} className="text-zinc-500 hover:text-white" aria-label="Close create menu">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {createMenu.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => { setShowCreateMenu(false); onSend(item.message); }}
                className="text-xs bg-[#181D25] hover:bg-[#20262F] border border-white/5 text-zinc-200 hover:text-white px-2.5 py-1.5 rounded-md transition-colors"
                data-testid={`kiri-create-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {suggestions.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => onSend(s.message)}
              className="text-[11px] bg-transparent border border-zinc-700 hover:border-[#FF5F15] text-zinc-400 hover:text-white px-2.5 py-1.5 rounded-full transition-colors"
              data-testid="kiri-suggestion"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {tipText && !tipDismissed && (
        <div className="mb-3 flex gap-2.5 bg-[#12161C] border border-[#FF5F15]/20 rounded-lg p-3" data-testid="kiri-did-you-know">
          <Lightbulb className="w-4 h-4 text-[#FF7A3D] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF7A3D]">Did you know?</div>
            <p className="text-xs text-zinc-300 mt-1">{tipText}</p>
            <div className="flex gap-3 mt-2">
              <button onClick={() => { dismissTip(); onSend(tipKey === "job" ? "Write today's site diary for this job" : "Help"); }} className="text-xs font-bold text-white hover:text-[#FF7A3D]" data-testid="kiri-tip-show-me">
                Show me
              </button>
              <button onClick={dismissTip} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="kiri-tip-not-now">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {recent && recent.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5 px-1">Recent</div>
          <div className="space-y-1">
            {recent.map((r, i) => {
              const meta = RECENT_META[r.result?.type];
              if (!meta) return null;
              const Icon = meta.Icon;
              return (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/[0.03]" data-testid="kiri-recent-item">
                  <Icon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="text-xs text-zinc-300 truncate flex-1">{meta.label(r.result.data)}</span>
                  <span className="text-[10px] text-zinc-500 shrink-0">{timeAgo(r.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
